import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Show } from '../../types/Show';
import { LoadingSkeleton } from '../ui/LoadingStates';
import {
  getOptimalImageSize,
  getImageVariantDetails,
  getDefaultSizesForImage,
  isMobileDevice,
  trackImagePerformance
} from '../../utils/imageUtils';
import { debugLog, debugWarn } from '../../utils/debugLog';
import { getCachedImage, isImageCacheAvailable, releaseObjectUrl } from '../../services/imageCacheService';
import fallbackShowImage from '../../assets/images/e-logo-600-min.png';

interface OptimizedShowImageProps {
  show: Show;
  size: 'small' | 'medium' | 'large';
  className?: string;
  alt?: string;
  imageClassName?: string;
  priority?: boolean; // For above-the-fold images
  preload?: boolean;
  preconnect?: boolean;
  sizes?: string;
  preferredVariants?: string[];
  onLoadComplete?: (loadTime: number) => void; // Performance tracking
  fallbackSrc?: string;
}

const OptimizedShowImage: React.FC<OptimizedShowImageProps> = ({
  show,
  size,
  className = '',
  alt,
  imageClassName = '',
  priority = false,
  preload = false,
  preconnect = false,
  sizes,
  preferredVariants,
  onLoadComplete,
  fallbackSrc
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [inView, setInView] = useState(process.env.NODE_ENV === 'test' || priority); // Always true in tests or for priority images
  const [isMobile, setIsMobile] = useState(isMobileDevice());
  const [isBlobSource, setIsBlobSource] = useState(false);

  // Handle responsive image sizing
  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer for lazy loading (skip in test environment or for priority images)
  useEffect(() => {
    if (process.env.NODE_ENV === 'test' || priority) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // Start loading 50px before entering viewport
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [priority]);

  // Get optimal image size based on device and size preference
  const variantDetails = useMemo(() => getImageVariantDetails(show.images), [show.images]);
  const preferredVariant = useMemo(() => {
    if (!preferredVariants?.length) {
      return undefined;
    }

    for (const key of preferredVariants) {
      const match = variantDetails.find((variant) => variant.key === key);
      if (match) {
        return match;
      }
    }
    return undefined;
  }, [preferredVariants, variantDetails]);

  const baseImageSrc = getOptimalImageSize(show.images, size, isMobile);
  const imageSrc = preferredVariant?.url ?? baseImageSrc;
  const selectedVariant = useMemo(() => {
    if (preferredVariant) {
      return preferredVariant;
    }
    return imageSrc ? variantDetails.find((variant) => variant.url === imageSrc) : variantDetails[0];
  }, [preferredVariant, imageSrc, variantDetails]);

  const srcSet = useMemo(() => {
    const responsiveCandidates = variantDetails.filter((variant) => Boolean(variant.width));
    if (!responsiveCandidates.length) {
      return '';
    }

    const maxWidthBySize: Record<OptimizedShowImageProps["size"], number | null> = {
      small: 300,
      medium: 400,
      large: null
    };

    const maxWidth = maxWidthBySize[size];
    const candidates = maxWidth
      ? responsiveCandidates.filter((variant) => (variant.width ?? 0) <= maxWidth)
      : responsiveCandidates;

    return candidates
      .map((variant) => `${variant.url} ${variant.width}w`)
      .join(', ');
  }, [size, variantDetails]);
  const resolvedSizes = sizes ?? getDefaultSizesForImage(size);
  const presenterName = show.presenter?.name || 'Unknown Presenter';
  const altText = alt || `${show.show} - ${presenterName}`;
  const fallbackImage = fallbackSrc || fallbackShowImage || process.env.REACT_APP_DEFAULT_SHOW_IMAGE || 
    "https://www.eruptionradio.uk/site/wp-content/uploads/2024/01/logomain.png";
  const targetImage = imageSrc || fallbackImage;
  const [resolvedSrc, setResolvedSrc] = useState<string>(targetImage);
  const loadStartRef = useRef(Date.now());
  const cacheHitRef = useRef(false);
  const objectUrlRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resolvedSrcRef = useRef(targetImage);
  const isLoadedRef = useRef(false);

  useEffect(() => {
    resolvedSrcRef.current = resolvedSrc;
  }, [resolvedSrc]);

  useEffect(() => {
    isLoadedRef.current = isLoaded;
  }, [isLoaded]);

  useEffect(() => {
    let isCancelled = false;
    const shouldBypassCache = priority;

    const applyResolvedSource = (finalSrc: string, cacheHit: boolean) => {
      if (isCancelled) return;

      cacheHitRef.current = cacheHit;
      setHasError(false);
      setIsBlobSource(finalSrc.startsWith('blob:'));

      if (finalSrc !== resolvedSrcRef.current) {
        resolvedSrcRef.current = finalSrc;
        setResolvedSrc(finalSrc);
        setIsLoaded(false);
        loadStartRef.current = Date.now();
      } else if (isLoadedRef.current) {
        // Image already loaded with this source; ensure skeleton does not linger
        setIsLoaded(true);
      } else {
        loadStartRef.current = Date.now();
      }
    };

    const resolveSource = async () => {
      if (!inView) {
        applyResolvedSource(targetImage, false);
        return;
      }

      if (shouldBypassCache || !imageSrc || !isImageCacheAvailable()) {
        if (objectUrlRef.current) {
          releaseObjectUrl(objectUrlRef.current);
          objectUrlRef.current = null;
        }
        setIsBlobSource(false);
        applyResolvedSource(targetImage, false);
        return;
      }

      try {
        const { url, cacheHit, objectUrl } = await getCachedImage(imageSrc);

        if (isCancelled) {
          if (objectUrl) {
            releaseObjectUrl(objectUrl);
          }
          return;
        }

        if (objectUrlRef.current && objectUrlRef.current !== objectUrl) {
          releaseObjectUrl(objectUrlRef.current);
        }

        objectUrlRef.current = objectUrl ?? null;
        applyResolvedSource(url, cacheHit);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          debugWarn('Image cache resolution failed, using fallback source', error);
        }

        if (objectUrlRef.current) {
          releaseObjectUrl(objectUrlRef.current);
          objectUrlRef.current = null;
        }

        setIsBlobSource(false);
        applyResolvedSource(targetImage, false);
      }
    };

    resolveSource();

    return () => {
      isCancelled = true;
    };
  }, [imageSrc, targetImage, inView, priority]);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      releaseObjectUrl(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const handleImageLoad = () => {
    const loadTime = Date.now() - loadStartRef.current;
    setIsLoaded(true);
    
    // Performance tracking callback
    if (onLoadComplete) {
      onLoadComplete(loadTime);
    }

    trackImagePerformance({
      loadTime,
      size,
      isMobile,
      cacheHit: cacheHitRef.current
    });
    
    // Log performance in development
    if (process.env.NODE_ENV === 'development') {
      debugLog(`Image loaded in ${loadTime}ms:`, resolvedSrc || targetImage);
    }
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!hasError) {
      const target = e.target as HTMLImageElement;
      target.src = fallbackImage;
      setHasError(true);
      setIsLoaded(true);
      cacheHitRef.current = false;
      setIsBlobSource(false);

      if (objectUrlRef.current) {
        releaseObjectUrl(objectUrlRef.current);
        objectUrlRef.current = null;
      }

      setResolvedSrc(fallbackImage);
      
      // Log error in development
      if (process.env.NODE_ENV === 'development') {
      debugWarn('Image failed to load, using fallback:', imageSrc);
      }
    }
  };

  const shouldRenderSrcSet = !isBlobSource && Boolean(srcSet);
  const widthAttr = selectedVariant?.width ?? undefined;
  const heightAttr = selectedVariant?.height ?? undefined;
  const canonicalSrc = imageSrc || fallbackImage;

  const canonicalOrigin = useMemo(() => {
    try {
      return new URL(canonicalSrc).origin;
    } catch {
      return null;
    }
  }, [canonicalSrc]);
  const isUsingFallback = !imageSrc || hasError;

  return (
    <>
      {(preconnect || preload) && canonicalSrc && (
        <Helmet>
          {preconnect && canonicalOrigin && (
            <link rel="preconnect" href={canonicalOrigin} crossOrigin="anonymous" />
          )}
          {preload && (
            <link
              rel="preload"
              as="image"
              href={canonicalSrc}
              {...(shouldRenderSrcSet ? { imagesrcset: srcSet, imagesizes: resolvedSizes } : {})}
            />
          )}
        </Helmet>
      )}
      <div ref={containerRef} className={`relative ${className}`}>
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 z-10">
            <LoadingSkeleton height="h-full" width="w-full" />
          </div>
        )}
        {isUsingFallback && <div className="absolute inset-0 bg-black" aria-hidden="true" />}
        
        {inView && (
          <img
            src={resolvedSrc || fallbackImage}
            alt={altText}
            loading={priority ? 'eager' : 'lazy'}
            // Use lowercase fetchpriority to avoid React warning
            {...(priority && { fetchpriority: 'high' })}
            decoding="async"
            className={`w-full h-full object-cover transition-opacity duration-300 ${isUsingFallback ? 'object-contain brightness-75' : ''} ${imageClassName}`}
            style={{ opacity: isLoaded ? 1 : 0 }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            width={widthAttr ?? undefined}
            height={heightAttr ?? undefined}
            {...(shouldRenderSrcSet
              ? { srcSet: srcSet, sizes: resolvedSizes }
              : { srcSet: undefined, sizes: undefined })}
          />
        )}
        {isUsingFallback && <div className="absolute inset-0 bg-black/55 pointer-events-none" aria-hidden="true" />}
      </div>
    </>
  );
};

export default OptimizedShowImage;
